import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const ConfirmDialogContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
    const [dialog, setDialog] = useState(null);
    const resolverRef = useRef(null);

    const close = useCallback((value) => {
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = null;
        }
        setDialog(null);
    }, []);

    const confirm = useCallback((options) => {
        const normalized = typeof options === 'string' ? { message: options } : (options || {});

        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setDialog({
                title: normalized.title || 'Please confirm',
                message: normalized.message || 'Are you sure you want to continue?',
                details: normalized.details,
                confirmText: normalized.confirmText || 'Confirm',
                cancelText: normalized.cancelText || 'Cancel',
                tone: normalized.tone || 'danger',
            });
        });
    }, []);

    useEffect(() => () => {
        if (resolverRef.current) resolverRef.current(false);
    }, []);

    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmDialogContext.Provider value={value}>
            {children}
            <ConfirmDialog
                isOpen={!!dialog}
                title={dialog?.title}
                message={dialog?.message}
                details={dialog?.details}
                confirmText={dialog?.confirmText}
                cancelText={dialog?.cancelText}
                tone={dialog?.tone}
                onConfirm={() => close(true)}
                onCancel={() => close(false)}
            />
        </ConfirmDialogContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmDialogContext);
    if (!ctx) {
        throw new Error('useConfirm must be used within ConfirmDialogProvider');
    }
    return ctx.confirm;
}
